// src/data/projectsClient.js
//
// PROJ-PR-1/PR-2 — Client for /api/projects and its child-object endpoints.
//
// All methods are async and return camelCase domain objects.
// The server already returns camelCase, so there is no field remapping
// at this boundary — just a thin transport layer with typed errors.
//
// Cache strategy: lightweight in-memory cache with 2-minute TTL.
// Writes invalidate the cache so the next read fetches fresh data.

import {
  AuthError,
  NetworkError,
  NotFoundError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import { log } from "../utils/logger.js";
import { weeklyText } from "../lib/weeklyRichText.js";
import { invalidateProjectWeekly } from "../services/projectWeeklyCache.js";

const BASE = "/api/projects";

// ─── Simple in-process cache ─────────────────────────────────────────────────

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes
const WEEKLY_PROSE_FIELDS = ["narrative", "accomplishments", "upcoming", "blockers", "pmoNote"];

let _cachedList = null;
let _cacheExpiry = 0;
let _cacheGeneration = 0;

function cacheHit() {
  return _cachedList !== null && Date.now() < _cacheExpiry;
}

function setCache(list) {
  _cachedList = list;
  _cacheExpiry = Date.now() + CACHE_TTL_MS;
}

export function invalidateCache() {
  _cacheGeneration++;
  invalidateProjectWeekly();
  _cachedList = null;
  _cacheExpiry = 0;
}

// ─── HTTP helpers ─────────────────────────────────────────────────────────────

async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (err) {
    throw new NetworkError(`Network error: ${err.message}`);
  }
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  const data = await res.json();
  if (options.method && options.method !== "GET") invalidateCache();
  return data;
}

// ─── Public API — projects ────────────────────────────────────────────────────

/**
 * Fetch all active projects with derived fields.
 * Results are cached for CACHE_TTL_MS milliseconds.
 * @returns {Promise<Array>}
 */
export async function getProjects() {
  if (cacheHit()) {
    log.debug?.("projectsClient: cache hit");
    return _cachedList;
  }
  const generation = _cacheGeneration;
  const list = await apiFetch(BASE);
  if (!Array.isArray(list)) {
    log.warn("projectsClient: unexpected non-array response from GET /api/projects");
    return [];
  }
  if (generation === _cacheGeneration) setCache(list);
  return list;
}

/**
 * Fetch a single project by UUID.
 * @param {string} id  UUID of the project
 * @returns {Promise<object>}  camelCase project object
 * @throws {NotFoundError} when project is not found
 */
export async function getProject(id) {
  if (!id || typeof id !== "string") {
    throw new ValidationError("getProject: id required");
  }
  return apiFetch(`${BASE}/${encodeURIComponent(id)}`);
}

export async function getProjectIntelProposals(projectId) {
  if (!projectId || typeof projectId !== "string") {
    throw new ValidationError("getProjectIntelProposals: projectId required");
  }
  const response = await apiFetch(`${BASE}/${encodeURIComponent(projectId)}/intel/proposals`);
  return Array.isArray(response) ? response : [];
}

export async function editProjectIntelProposal(projectId, proposalId, proposedValue) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/intel/proposals/${encodeURIComponent(proposalId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ proposedValue }),
  });
}

export async function approveProjectIntelProposal(projectId, proposalId) {
  const response = await apiFetch(
    `${BASE}/${encodeURIComponent(projectId)}/intel/proposals/${encodeURIComponent(proposalId)}/approve`,
    { method: "POST" },
  );
  invalidateCache();
  return response;
}

export async function dismissProjectIntelProposal(projectId, proposalId) {
  return apiFetch(
    `${BASE}/${encodeURIComponent(projectId)}/intel/proposals/${encodeURIComponent(proposalId)}/dismiss`,
    { method: "POST" },
  );
}

export async function extractProjectIntel(projectId, sources) {
  if (!projectId || typeof projectId !== "string") {
    throw new ValidationError("extractProjectIntel: projectId required");
  }
  if (!Array.isArray(sources) || sources.length === 0) {
    throw new ValidationError("extractProjectIntel: sources required");
  }
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/intel/extract`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sources }),
  });
}

/**
 * Fetch active category budgets for a single project.
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export async function getProjectBudgets(projectId) {
  if (!projectId || typeof projectId !== "string") {
    throw new ValidationError("getProjectBudgets: projectId required");
  }
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/budgets`);
}

export async function createProjectBudget(projectId, payload) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/budgets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function updateProjectBudget(projectId, budgetId, patch) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/budgets/${encodeURIComponent(budgetId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export async function deleteProjectBudget(projectId, budgetId) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/budgets/${encodeURIComponent(budgetId)}`, {
    method: "DELETE",
  });
}

/**
 * Update an existing project (partial PATCH).
 * Invalidates the list cache on success.
 * @param {string} projectId  UUID of the project
 * @param {object} patch      camelCase subset of editable fields
 * @returns {Promise<object>}  the updated project
 */
export async function archiveProject(id, reason) {
  if (!id || typeof id !== "string") {
    throw new ValidationError("archiveProject: id required");
  }
  return apiFetch(`${BASE}/${encodeURIComponent(id)}/archive`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
}

export async function updateProject(projectId, patch) {
  if (!projectId || typeof projectId !== "string") {
    throw new ValidationError("updateProject: projectId required");
  }
  const updated = await apiFetch(`${BASE}/${encodeURIComponent(projectId)}`, {
    method:  "PATCH",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(patch),
  });
  invalidateCache();
  log.ui("projectsClient: updated project", { id: projectId });
  return updated;
}

export async function getProjectDocuments(projectId) {
  if (!projectId || typeof projectId !== "string") {
    throw new ValidationError("getProjectDocuments: projectId required");
  }
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/documents`);
}

export async function getProjectSourceInventory() {
  const response = await apiFetch(`${BASE}/sources/inventory`);
  return {
    portfolioSources: Array.isArray(response?.portfolioSources) ? response.portfolioSources : [],
    projectSources: Array.isArray(response?.projectSources) ? response.projectSources : [],
  };
}

export async function getPortfolioSources() {
  const response = await apiFetch(`${BASE}/sources`);
  return Array.isArray(response) ? response : [];
}

export async function getProjectSources(projectId, { effective = true } = {}) {
  if (!projectId || typeof projectId !== "string") {
    throw new ValidationError("getProjectSources: projectId required");
  }
  const suffix = effective ? "sources" : "sources/owned";
  const response = await apiFetch(`${BASE}/${encodeURIComponent(projectId)}/${suffix}`);
  return Array.isArray(response) ? response : [];
}

export async function createProjectSource(projectId, payload) {
  if (!payload || typeof payload !== "object") {
    throw new ValidationError("createProjectSource: payload required");
  }
  const path = projectId
    ? `${BASE}/${encodeURIComponent(projectId)}/sources`
    : `${BASE}/sources`;
  const created = await apiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  invalidateCache();
  return created;
}

export async function deleteProjectSource(sourceId, projectId = null) {
  if (!sourceId || typeof sourceId !== "string") {
    throw new ValidationError("deleteProjectSource: sourceId required");
  }
  const path = projectId
    ? `${BASE}/${encodeURIComponent(projectId)}/sources/${encodeURIComponent(sourceId)}`
    : `${BASE}/sources/${encodeURIComponent(sourceId)}`;
  const deleted = await apiFetch(path, { method: "DELETE" });
  invalidateCache();
  return deleted;
}

/**
 * Read the project's server-side Slack source status and recent messages.
 * The response never contains the bot token or raw Slack API envelope.
 */
export async function getProjectSlackSource(projectId) {
  if (!projectId || typeof projectId !== "string") {
    throw new ValidationError("getProjectSlackSource: projectId required");
  }
  const response = await apiFetch(`${BASE}/${encodeURIComponent(projectId)}/slack`);
  return response?.source ?? {
    provider: "slack",
    type: "channel",
    reference: null,
    configured: false,
    readable: false,
    status: "unconfigured",
    reason: "invalid_response",
    messages: [],
    messageCount: 0,
  };
}

export async function createProjectDocument(projectId, payload) {
  if (!projectId || typeof projectId !== "string") {
    throw new ValidationError("createProjectDocument: projectId required");
  }
  const created = await apiFetch(`${BASE}/${encodeURIComponent(projectId)}/documents`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  invalidateCache();
  return created;
}

export async function updateProjectDocument(projectId, documentId, patch) {
  const updated = await apiFetch(
    `${BASE}/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  invalidateCache();
  return updated;
}

export async function deleteProjectDocument(projectId, documentId) {
  const deleted = await apiFetch(
    `${BASE}/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`,
    { method: "DELETE" },
  );
  invalidateCache();
  return deleted;
}

/**
 * Create a new project.
 * Invalidates the list cache on success.
 * @param {object} payload  camelCase project fields (see validateNewProject)
 * @returns {Promise<object>}  the created project
 */
export async function createProject(payload) {
  if (!payload || typeof payload !== "object") {
    throw new ValidationError("createProject: payload required");
  }
  const created = await apiFetch(BASE, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(payload),
  });
  invalidateCache();
  log.ui("projectsClient: created project", { id: created.id });
  return created;
}

// ─── Public API — decisions ───────────────────────────────────────────────────

/**
 * Fetch all open decisions across the portfolio (for Decisions Needed tab).
 * @returns {Promise<Array>}
 */
export async function getAllOpenDecisions() {
  return apiFetch(`${BASE}/decisions`);
}

/**
 * Fetch decisions for a single project.
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export async function getProjectDecisions(projectId) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/decisions`);
}

/**
 * Create a decision for a project. editor+.
 * @param {string} projectId
 * @param {object} payload
 * @returns {Promise<object>}
 */
export async function createDecision(projectId, payload) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/decisions`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(payload),
  });
}

/**
 * Update a decision. editor+.
 * @param {string} projectId
 * @param {string} decisionId
 * @param {object} patch
 * @returns {Promise<object>}
 */
export async function updateDecision(projectId, decisionId, patch) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/decisions/${encodeURIComponent(decisionId)}`, {
    method:  "PATCH",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(patch),
  });
}

// ─── Public API — risks ───────────────────────────────────────────────────────

/**
 * Fetch all open risks across the portfolio (for Risk Register tab).
 * @returns {Promise<Array>}
 */
export async function getAllOpenRisks() {
  return apiFetch(`${BASE}/risks`);
}

/**
 * Fetch risks for a single project.
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export async function getProjectRisks(projectId) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/risks`);
}

/**
 * Create a risk for a project. editor+.
 */
export async function createRisk(projectId, payload) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/risks`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(payload),
  });
}

/**
 * Update a risk. editor+.
 */
export async function updateRisk(projectId, riskId, patch) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/risks/${encodeURIComponent(riskId)}`, {
    method:  "PATCH",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(patch),
  });
}

// ─── Public API — milestones ──────────────────────────────────────────────────

/**
 * Fetch next milestone per project (for Milestones portfolio tab).
 * @returns {Promise<Array>}
 */
export async function getAllNextMilestones() {
  return apiFetch(`${BASE}/milestones`);
}

/**
 * Fetch milestones for a single project.
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export async function getProjectMilestones(projectId) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/milestones`);
}

/**
 * Create a milestone for a project. editor+.
 */
export async function createMilestone(projectId, payload) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/milestones`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(payload),
  });
}

/**
 * Update a milestone. editor+.
 */
export async function updateMilestone(projectId, milestoneId, patch) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/milestones/${encodeURIComponent(milestoneId)}`, {
    method:  "PATCH",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(patch),
  });
}

// ─── Public API — weekly updates ─────────────────────────────────────────────

/**
 * Fetch weekly updates for a single project, newest first.
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export async function getProjectWeeklyUpdates(projectId) {
  const rows = await apiFetch(`${BASE}/${encodeURIComponent(projectId)}/weekly-updates`);
  // This endpoint feeds legacy text-only record/report consumers. The Updates
  // overview retains the restricted representation for editing and display.
  return rows.map(row => {
    const plain = { ...row };
    for (const key of WEEKLY_PROSE_FIELDS) if (plain[key] != null) plain[key] = weeklyText(plain[key]);
    return plain;
  });
}

/**
 * Create a weekly update. editor+.
 */
export async function createWeeklyUpdate(projectId, payload) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/weekly-updates`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(payload),
  });
}

// ─── Public API — project tracks ─────────────────────────────────────────────

/**
 * Fetch all tracks for a single project.
 * @param {string} projectId
 * @returns {Promise<Array>} Four track objects in track_key order
 */
export async function getProjectTracks(projectId) {
  return apiFetch(`${BASE}/${encodeURIComponent(projectId)}/tracks`);
}

/**
 * Update a single track field-set. editor+.
 * Invalidates the project cache on success so the next read is fresh.
 * @param {string} projectId
 * @param {string} trackKey  one of the 7 canonical keys
 * @param {object} patch     { status?, owner?, note? }
 * @returns {Promise<object>}  updated track object (camelCase)
 */
export async function updateProjectTrack(projectId, trackKey, patch) {
  const updated = await apiFetch(
    `${BASE}/${encodeURIComponent(projectId)}/tracks/${encodeURIComponent(trackKey)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }
  );
  invalidateCache();
  log.ui("projectsClient: track updated", { projectId, trackKey });
  return updated;
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Capex-relevant project types per PROJ-PR-1 spec. Used for the CapEx stat
// on the Locations dashboard (PortfolioView.jsx) and the filter helpers.
export const CAPEX_PROJECT_TYPES = new Set([
  "new_build", "renovation", "critical_infra", "compliance",
]);

/**
 * Count active projects of capex-relevant types from an already-loaded list.
 * Capex types: new_build, renovation, critical_infra, compliance.
 * Active = phase != 'complete' and not deleted.
 *
 * Exported as a pure function so PortfolioView can compute the stat without
 * a second fetch (getProjects() is cache-aware).
 *
 * @param {Array} projects  result of getProjects()
 * @returns {number}
 */
export function countCapexActiveProjects(projects) {
  if (!Array.isArray(projects)) return 0;
  return projects.filter(
    (p) => CAPEX_PROJECT_TYPES.has(p.projectType) && p.phase !== "complete"
  ).length;
}
